<template lang="pug">
.family-message-history-detail-message-register-detail-accordion
  b-card-header#reply-list.p-2(header-tag="header" role="tab" header-class="accordion-toggle" v-b-toggle.accordion-reply-list)
    span.toggle-text 返信リスト（未読：{{ unreadCount }}件）
  b-collapse#accordion-reply-list(accordion="reply-list-accordion" role="tabpanel")
    b-card-body.p-2
      span(v-show="!isBusy && replyItems.length < 1") 返信はありません。
      MessageHistoryReplyList(v-model="replyItems" :isBusy="isBusy" @onMessage="onReplyMessage" @onReadSwitch="onReadSwitch")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore } from "@/store/store";
import { MobileMessageHistoryState } from "@/store/MobileMessageHistory";

import OtherUserGroupSendMessageRepliesService, { SendMessageReplyModel, SendMessageReplySenderType } from "@/commons/services/OtherUserGroupSendMessageReplies";
import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";

import MessageHistoryReplyList, { MessageHistoryReplyListItem, MessageHistoryReplyListType } from "@/mobile/components/lists/MessageHistoryReplyList.vue";

/**
 * ### 送信履歴詳細(家族)返信リストアコーディオン
 */
@Component({
  components: {
    MessageHistoryReplyList
  }
})
export default class MessageReplyListAccordion extends Vue {
  /** メッセージ送信の返信サービス */
  @Inject("OtherUserGroupSendMessageRepliesService") otherUserGroupSendMessageRepliesService!: OtherUserGroupSendMessageRepliesService;
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;

  /** 新規送信入力画面での入力情報 */
  @Getter("mobileMessageHistoryState") state!: MobileMessageHistoryState;

  /** ローディングの表示可否 */
  isBusy: boolean = false;
  /** 返信未読数 */
  unreadCount: number = 0;
  /** 施設関係者グループ種類MAP */
  otherUserGroupTypeMap: Map<number, string> = new Map<number, string>();
  /** 返信メッセージ */
  replyItems: Array<MessageHistoryReplyListItem> = [];

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - メッセージの返信を表示するコンポーネント用の情報を作成する
   * - 画面表示用に返信未読数をカウントする
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    // 後で使用するため、施設関係者グループ種類をMAP化しておく
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherGroupTypes();
    const otherUserGroupTypeMap: Map<number, string> = this.createOtherUserGroupTypesMap(otherUserGroupTypes);
    this.otherUserGroupTypeMap = otherUserGroupTypeMap;

    // メッセージの返信を表示するコンポーネント用の情報を作成する
    const replyMessages: Array<SendMessageReplyModel> = await this.getReplyMessages();
    const replyItems: Array<MessageHistoryReplyListItem> = this.createReplyItems(replyMessages, otherUserGroupTypeMap);
    this.replyItems = replyItems;

    // 画面表示用に返信未読数をカウントする
    let unreadCount = 0;
    for (const message of replyItems) {
      if (message.readed == false) {
        unreadCount++;
      }
    }
    this.unreadCount = unreadCount;

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);

    return otherUserGroupTypes;
  }

  /**
   * ### 画面表示用に施設関係者グループ種類のMAPを作成する
   */
  public createOtherUserGroupTypesMap(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();

    for (const type of otherUserGroupTypes) {
      map.set(type.id, type.name);
    }

    return map;
  }

  /**
   * ### メッセージに対する返信を取得する
   */
  public async getReplyMessages(): Promise<Array<SendMessageReplyModel>> {
    const replyMessages: Array<SendMessageReplyModel> = await this.otherUserGroupSendMessageRepliesService.get(userStore, Number(this.state.datetime));
    return replyMessages;
  }

  /**
   * ### 返信リストの行情報を作成する
   */
  public createReplyItems(replies: Array<SendMessageReplyModel>, otherUserGroupTypeMap: Map<number, string>): Array<MessageHistoryReplyListItem> {
    // 返信メッセージの1行はユーザーの1番古いデータになるので、datetimeの古い順にソートする
    const sortedReplies: Array<SendMessageReplyModel> = replies.sort((a, b) => a.datetime - b.datetime);

    // ユーザー単位でメッセージをMAP化する
    let map: Map<string, Array<SendMessageReplyModel>> = new Map<string, Array<SendMessageReplyModel>>();
    for (const reply of sortedReplies) {
      const messages: Array<SendMessageReplyModel> | undefined = map.get(`${reply.groupId}_${reply.memberId}`);
      if (messages) {
        messages.push(reply);
        map.set(`${reply.groupId}_${reply.memberId}`, messages);
      } else {
        map.set(`${reply.groupId}_${reply.memberId}`, [reply]);
      }
    }

    // MAP化したものを行情報にする
    let items: Array<MessageHistoryReplyListItem> = [];
    map.forEach((value: Array<SendMessageReplyModel>) => {
      const sortedValue: Array<SendMessageReplyModel> = value.sort((a, b) => a.datetime - b.datetime);

      // 行の並び順は最後のメッセージの日付の新しいもの順にしたいので、行情報は最新のものを使用する
      const index: number = sortedValue.length - 1;

      // 未読かどうかは家族の返信で未読が存在するか否かで判断する
      let readed: boolean = true;
      for (const value of sortedValue) {
        if (value.senderType == SendMessageReplySenderType.OTHER_USER && value.readUserIds.indexOf(userStore.loginUser.userId) < 0) {
          readed = false;
          break;
        }
      }

      // 返信のグループ名を格納する
      const groupTypeName: string | undefined = otherUserGroupTypeMap.get(sortedValue[index].groupTypeId);

      items.push({
        parentMessagesSortKey: sortedValue[index].parentMessagesSortKey,
        groupId: sortedValue[index].groupId,
        memberId: sortedValue[index].memberId,
        mainName: sortedValue[index].groupMemberName,
        subTitle: `${groupTypeName}・${sortedValue[index].groupName}`,
        subName: `${groupTypeName}・${sortedValue[index].groupName}・${sortedValue[index].groupMemberOrganization}`,
        datetime: sortedValue[index].datetime,
        readed: readed,
        type: sortedValue[index].type as MessageHistoryReplyListType,
        messages: value
      });
    });

    // 画面表示では日付の新しい順なのでソートしなおす
    const sortedItems: Array<MessageHistoryReplyListItem> = items.concat().sort((a, b) => b.datetime - a.datetime);

    return sortedItems;
  }

  /**
   * ### 返信の未読押下イベント
   *
   * - 画面表示用の返信未読数を更新するためデクリメントする
   * - 親コンポーネントに返信の未読が押下されたことを通知する
   */
  public onReplyMessage(message: MessageHistoryReplyListItem): void {
    if (message.readed == false) {
      this.unreadCount = this.unreadCount - 1;
    }
    this.$emit("onReplyMessage", message);
    return;
  }

  /**
   * ### 未読／既読のスイッチ押下イベント
   *
   * - 親コンポーネントに押下されたことを通知する
   */
  public onReadSwitch(message: MessageHistoryReplyListItem): void {
    this.$emit("onReadSwitch", message);
    return;
  }
}
</script>
